import { createLogger } from '../config/logger';

const logger = createLogger('dateFormatters');

/**
 * Formats birthday string to readable format
 * @param {string} birthdayString - Birthday in YYYY-MM-DD format
 * @returns {string} Formatted date or '—' if invalid
 */
export const formatBirthday = (birthdayString) => {
    if (!birthdayString) {
        return '—';
    }

    try {
        // Handle both date-only strings ("2000-10-10") and ISO timestamps ("2000-10-10T00:00:00.000Z")
        let dateStr = String(birthdayString);
        if (dateStr.includes('T')) {
            // Extract just the date part from ISO string
            dateStr = dateStr.split('T')[0];
        }

        // Parse the date string (format: "YYYY-MM-DD")
        const parts = dateStr.split('-');
        if (parts.length !== 3) {
            return '—';
        }

        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const day = parseInt(parts[2], 10);

        if (isNaN(year) || isNaN(month) || isNaN(day)) {
            return '—';
        }
        if (month < 1 || month > 12 || day < 1 || day > 31) {
            return '—';
        }

        // Create date in local timezone (month is 0-indexed in Date constructor)
        const date = new Date(year, month - 1, day);

        // Validate the date was created correctly
        if (
            date.getFullYear() !== year ||
            date.getMonth() !== month - 1 ||
            date.getDate() !== day
        ) {
            return '—';
        }

        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: '2-digit',
        });
    } catch (error) {
        logger.error('Error formatting birthday', { error, birthdayString });
        return '—';
    }
};

/**
 * Formats date string to locale date string
 * @param {string} dateString - Date string
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date or '—' if invalid
 */
export const formatDate = (dateString, options = {}) => {
    if (!dateString) {
        return '—';
    }
    try {
        const date = new Date(dateString);
        // Check if date is valid (Invalid Date objects have NaN as their time value)
        if (isNaN(date.getTime())) {
            return '—';
        }
        return date.toLocaleDateString('en-US', options);
    } catch (error) {
        return '—';
    }
};

/**
 * Gets month name from month number
 * @param {number} monthNumber - Month number (1-12)
 * @returns {string} Month name
 */
export const formatMonthName = (monthNumber) => {
    return new Date(2000, parseInt(monthNumber) - 1, 1).toLocaleString('default', {
        month: 'long',
    });
};

